import React from "react";
import Select from "react-select";
import { Button, Input } from "@wac-ui-dashboard/wac_component_library";
import { getFormatedBusinessDateTime } from "../../../../utils/Functions/table";
import {
  needsApprovalOf,
  sumBy,
  SYNC_STATUS_CLASS as STATUS_CLASS,
  SYNC_STATUS_LABEL as STATUS_LABEL,
} from "../../../../utils/Functions/posistSync";
import useDeltaMenuSync from "./useDeltaMenuSync";

const StoreRow = ({ store }) => {
  const status = store?.status ?? "pending";

  return (
    <div className="pro-d-flex pro-justify-between pro-items-center pro-py-2 pro-border-bottom">
      <div>
        <span className="pro-fw-medium">{store?.store_name}</span>
        {store?.seeded && (
          <span className="pro-font-xs pro-text-muted pro-ms-2">
            (mirror seeded)
          </span>
        )}
        {status === "success" && (
          <div className="pro-font-xs pro-text-muted">
            {`${store?.changed_items ?? 0} changed items → ${
              store?.staged ?? 0
            } compared`}
            {(store?.deleted ?? 0) > 0 && `, ${store.deleted} delisted`}
            {", "}
            <span className="pro-fw-medium">
              {`${needsApprovalOf(store)} need approval`}
            </span>
          </div>
        )}
        {(status === "failed" || status === "skipped") && store?.message && (
          <div className="pro-font-xs pro-text-muted">{store.message}</div>
        )}
      </div>
      <span className={`pro-font-sm ${STATUS_CLASS[status] ?? ""}`}>
        {STATUS_LABEL[status] ?? status}
      </span>
    </div>
  );
};

const DeltaMenuSync = ({ show, closeModal }) => {
  const {
    stores,
    selectedStores,
    setSelectedStores,
    notifyEmail,
    setNotifyEmail,
    lastSyncTime,
    setLastSyncTime,
    maxSyncTime,
    run,
    isStarting,
    finishedCount,
    handleSelectAll,
    handleClearAll,
    handleStart,
    handleStartAnother,
  } = useDeltaMenuSync({ show });

  const allSelected =
    stores.length > 0 && selectedStores.length === stores.length;

  // One store at a time, ~50s each. Rounded up and called "around" because the
  // real figure swings with how big each store's menu is.
  const estimatedMinutes = Math.max(
    1,
    Math.ceil(selectedStores.length * (50 / 60))
  );

  // Picker view - no run started yet.
  if (!run) {
    return (
      <>
        <div className="col-auto pro-mt-3">
          <div className="pro-d-flex pro-justify-between pro-items-center pro-mb-1">
            <label htmlFor="" className="pro-font-sm pro-fw-medium pro-mb-0">
              Stores
            </label>
            {/* Plain buttons rather than a react-select option row: an "All
                stores" entry inside the list can be selected alongside named
                stores, which then reads as a contradiction. */}
            <button
              type="button"
              className="pro-btn pro-btn-link pro-p-0 pro-font-xs"
              onClick={allSelected ? handleClearAll : handleSelectAll}
              disabled={!stores.length}
            >
              {allSelected ? "Clear all" : `Select all ${stores.length}`}
            </button>
          </div>
          <Select
            id="delta_stores"
            isMulti
            isClearable
            options={stores}
            getOptionLabel={(option) => `${option.name}`}
            getOptionValue={(option) => `${option["customer_key"]}`}
            className={`pro-input lg multi-select`}
            placeholder={"Select one or more stores"}
            classNamePrefix="pro-input"
            value={selectedStores}
            onChange={(value) => setSelectedStores(value ?? [])}
          />
          <div className="pro-font-xs pro-text-muted pro-mt-2">
            Each store takes roughly 35-70 seconds, one at a time, starting
            within a minute.
            {selectedStores.length > 2 &&
              ` Around ${estimatedMinutes} minute${
                estimatedMinutes === 1 ? "" : "s"
              } for ${selectedStores.length} stores.`}{" "}
            The sync runs in the background, so you can close this and come back
            to it. Changes are staged for approval - nothing reaches customers
            until approved.
          </div>
        </div>

        <div className="col-auto pro-mt-4">
          <Input
            label={"Email the result to (optional)"}
            type="text"
            id="delta_notify_email"
            name="delta_notify_email"
            className={`pro-input lg`}
            placeholder={"name@cariboucoffee-me.com"}
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
          />
          <div className="pro-font-xs pro-text-muted pro-mt-1">
            One summary sent once every store has finished, with the per-store
            result. Leave blank for no email.
          </div>
        </div>

        <div className="col-auto pro-mt-4">
          {/* A raw input rather than the shared Input: datetime-local is what
              gives a date and time picker, and the API's epoch milliseconds are
              converted from it on the way out. max= is now, since a window
              opening in the future would end before it starts. */}
          <label
            htmlFor="delta_last_sync_time"
            className="pro-font-sm pro-fw-medium pro-mb-1 pro-d-block"
          >
            Sync from (optional)
          </label>
          <input
            type="datetime-local"
            id="delta_last_sync_time"
            name="delta_last_sync_time"
            className={`pro-input lg`}
            value={lastSyncTime}
            max={maxSyncTime}
            onChange={(e) => setLastSyncTime(e.target.value)}
          />
          <div className="pro-font-xs pro-text-muted pro-mt-1">
            Kuwait time. Asks Posist for everything changed since that moment,
            for every store selected. Leave blank to carry on from each store's
            own last successful sync, which is what a normal run does.
          </div>
        </div>

        <div
          className={`col-12 pro-d-flex pro-justify-end pro-mt-4 offcanvas-footer-sticky-btns`}
        >
          <Button
            className={"pro-btn-link lg pro-px-5"}
            onClick={() => closeModal()}
          >
            Cancel
          </Button>
          <Button
            className={`pro-btn-primary lg pro-ms-3 ${
              isStarting ? "loading" : ""
            }`}
            type="submit"
            disabled={!selectedStores.length || isStarting}
            onClick={handleStart}
          >
            {selectedStores.length > 1
              ? `Sync ${selectedStores.length} Stores`
              : `Sync Store`}
          </Button>
        </div>
      </>
    );
  }

  // Progress view.
  return (
    <>
      <div className="col-auto pro-mt-3">
        <div className="pro-d-flex pro-justify-between pro-items-center pro-mb-3">
          <span className="pro-fw-medium">
            {run?.complete
              ? `Finished ${run?.total} store${run?.total === 1 ? "" : "s"}`
              : `${finishedCount} of ${run?.total} complete`}
          </span>
          {!run?.complete && <span className="pro-font-sm">Running…</span>}
        </div>

        {run?.last_sync_time && (
          // Read back in the shape it was picked in. The server stores and
          // returns the epoch milliseconds it works in, which is not something
          // the operator ever typed and not something they can check at a glance.
          <div className="pro-font-xs pro-text-muted pro-mb-3">
            {`Manual window: everything changed since
            ${getFormatedBusinessDateTime(run.last_sync_time)} Kuwait time, not
            each store's own last sync.`}
          </div>
        )}

        <div>
          {(run?.stores ?? []).map((store) => (
            <StoreRow key={store?.customer_key} store={store} />
          ))}
        </div>

        {run?.complete && (
          // The same totals the report mail leads on, so an operator reading
          // the panel and an operator reading the mail are told the same
          // thing. Only shown once every store is terminal - a running total
          // would climb as workers land and read as a number that changed.
          <div className="pro-font-sm pro-mt-3">
            <span className="pro-fw-medium">
              {`${sumBy(run.stores, needsApprovalOf)} product(s) need approval`}
            </span>
            {/* Beside the approvals, never inside them: a delisted product is
                not in the "Changed only" list and cannot be approved, so
                folding it into the count would send an admin looking for rows
                that are not there. */}
            {sumBy(run.stores, (store) => store?.deleted) > 0 &&
              `, plus ${sumBy(
                run.stores,
                (store) => store?.deleted
              )} Posist has deleted (not listed for approval)`}
            {`, from ${sumBy(
              run.stores,
              (store) => store?.changed_items
            )} changed menu item(s).`}
          </div>
        )}

        {run?.notify_email && (
          // Driven by what the server actually recorded, not by "the run finished
          // and an address was typed" - that said the summary had been emailed on
          // runs where the mail was never even attempted.
          <div
            className={`pro-font-xs pro-mt-3 ${
              run?.notified === false ? "pro-text-danger" : "pro-text-muted"
            }`}
          >
            {run?.notified === true &&
              `Summary emailed to ${run.notify_email}.`}
            {run?.notified === false &&
              `The summary could not be emailed to ${run.notify_email}. The sync
               itself is unaffected - the results above are final.`}
            {run?.notified == null &&
              `${run.notify_email} will be emailed once every store has
               finished, so this panel can be closed.`}
          </div>
        )}

        {run?.complete && (run?.counts?.failed ?? 0) > 0 && (
          <div className="pro-font-sm pro-text-danger pro-mt-3">
            {`${run.counts.failed} store${
              run.counts.failed === 1 ? "" : "s"
            } failed. Re-running a failed store is safe - the sync window only
            advances on success.`}
          </div>
        )}
      </div>

      <div
        className={`col-12 pro-d-flex pro-justify-end pro-mt-4 offcanvas-footer-sticky-btns`}
      >
        <Button
          className={"pro-btn-link lg pro-px-5"}
          onClick={() => closeModal()}
        >
          Close
        </Button>
        <Button
          className={`pro-btn-primary lg pro-ms-3`}
          disabled={!run?.complete}
          onClick={handleStartAnother}
        >
          Sync More Stores
        </Button>
      </div>
    </>
  );
};

export default DeltaMenuSync;
